/* eslint-disable */
/**
 * @fileoverview Quick Actions Component
 *
 * Displays quick action buttons/links based on user permissions.
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    FiClock,
    FiDollarSign,
    FiUsers,
    FiPieChart,
    FiBook
} from 'react-icons/fi';

/**
 * QuickActions Component
 *
 * @param {Object} props
 * @param {Object} props.permissions - User permissions object
 * @returns {JSX.Element|null}
 */
const QuickActions = memo(({ permissions }) => {
    // Generate quick links based on user permissions
    const quickLinks = useMemo(() => {
        const links = [];

        // Time tracking - available to users who can view time entries
        if (permissions.canViewTimeEntries) {
            links.push({
                name: 'Time & Expense',
                icon: <FiClock />,
                href: '/profile',
                description: 'Record Your Time'
            });
        }

        // Client management
        if (permissions.canViewClients) {
            links.push({
                name: 'Manage Clients',
                icon: <FiUsers />,
                href: '/clients/manage',
                description: 'Client Operations'
            });
        }

        // Billing
        if (permissions.canViewBilling) {
            links.push({
                name: 'Billing',
                icon: <FiDollarSign />,
                href: '/billing',
                description: 'View Client Totals'
            });
        }

        // AR Ledger
        if (permissions.canViewArLedger) {
            links.push({
                name: 'AR Ledger',
                icon: <FiBook />,
                href: '/ledger',
                description: 'Accounts Receivable'
            });
        }

        // Reports
        if (permissions.canViewReports) {
            links.push({
                name: 'Reports',
                icon: <FiPieChart />,
                href: '/reports',
                description: 'View Client Reports'
            });
        }

        return links;
    }, [permissions]);

    // Don't render if no links available
    if (quickLinks.length === 0) {
        return null;
    }

    return (
        <section className="dashboard-quicklinks">
            <h2 className="d-section-title">Quick Actions</h2>
            <div className="quicklinks-grid">
                {quickLinks.map((link, index) => (
                    <Link to={link.href} className="dashb quicklink-card" key={index}>
                        <div className="quicklink-icon">{link.icon}</div>
                        <div className="quicklink-info">
                            <h3>{link.name}</h3>
                            <p>{link.description}</p>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
});

QuickActions.displayName = 'QuickActions';

export default QuickActions;
